import { ActionButton } from '../components/ActionButton';
import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import Button from '@material-ui/core/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';

import DialogWithEnterAction from '../components/DialogWithEnterAction';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { useTheme } from '@material-ui/core/styles';

import { useTranslation } from 'react-i18next';
import momentWithLocale, { diff } from '../local_moment';

import Typography from '@material-ui/core/Typography';
import { GraphSlider } from '../FormWidgets/MySlider';
import {
  HistoryResSelect,
  DecimalSelect,
  DelimiterSelect,
  TimeFormatSelect
} from '../FormWidgets/Select';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { inital_data, resolution_mapping } from './Graph/graph_config';
import { HISTORY_VALUES, resolutions } from '../Constants/constants';
import { HandleAction, ParseFloat, PrintDate, isZoned } from '../Utils/utils';
import { getHistoryTranslationKey } from '../Utils/history_display_utils';
import { omitGraphErrorValues } from '../Utils/chart_utils';
import {
  abandonHistoryTransfer,
  beginHistoryRequest,
  cancelHistoryRequest,
  HISTORY_INACTIVITY_TIMEOUT_MS,
  HISTORY_RECOVERY_RETRY_DELAY_MS,
  normalizeHistoryResolution,
  shouldRetryHistoryRequest,
  supportsStableHistoryTransferId
} from '../Utils/history_utils';

import getStateManager from '../StateManager.js';
import { useSnapshot, proxy, subscribe } from 'valtio';
import { subscribeKey } from 'valtio/utils';
import { useAddress } from '../Hooks/addressHook';
import { useSnackbar } from 'notistack';

import _ from 'lodash';

function LineChart({ data, options, plugins }) {
  return <Line data={data} options={options} plugins={plugins} />;
}
const MemorizedChart = React.memo(LineChart);

function CalcLabelFormat(labels) {
  if (labels.length <= 1) return 'HH:mm:ss';

  const first = momentWithLocale(labels[0]);
  const last = momentWithLocale(labels[labels.length - 1]);

  // For multi-day ranges, always include the date so timestamps don't look mixed.
  if (first.format('L') !== last.format('L')) {
    return 'D MMM HH:mm';
  }

  return 'HH:mm:ss';
}

function SliceData(labels, datasets, start, end, controller, resolution) {
  // slice labels and data.
  for (let i = 0; i < datasets.length; i++) {
    datasets[i].data = omitGraphErrorValues(datasets[i].data.slice(start, end));
    datasets[i].spanGaps = true;
  }
  labels = labels.slice(start, end);

  // Keep the old zero-value hotfix only for recent/live history. On daily/full
  // it can punch holes into the loaded range and make the graph look truncated.
  if (resolution === resolutions.recent && labels.length > 0 && datasets.length > 0) {
    const filteredLabels = [];
    const filteredDataPerDs = datasets.map(() => []);

    for (let i = 0; i < labels.length; i++) {
      let zeroCount = 0;
      for (const label of ['temp', 'humidity']) {
        const ds = datasets.find((d) => d.label === label);
        const v = ds.data[i];
        const n = typeof v === 'number' ? v : Number.parseFloat(v);
        const isZero = Number.isFinite(n) ? n === 0 : v === 0;
        if (isZero) {
          zeroCount++;
        }
      }
      // keep if fewer than 2 zeros (from non-ignored datasets)
      if (zeroCount < 2) {
        filteredLabels.push(labels[i]);
        for (let d = 0; d < datasets.length; d++) filteredDataPerDs[d].push(datasets[d].data[i]);
      }
    }

    labels = filteredLabels;
    for (let d = 0; d < datasets.length; d++) datasets[d].data = filteredDataPerDs[d];
  }

  // Convert timestamp.
  const first_ts = momentWithLocale(labels[0]);
  const last_ts = momentWithLocale(labels[labels.length - 1]);

  // Get formats for timestamps and for view-display at the top of the page.
  const format = CalcLabelFormat(labels);
  const view_format = first_ts.format('L') !== last_ts.format('L') ? 'lll' : 'HH:mm:ss';

  // Set format for all timestamps
  for (let i = 0; i < labels.length; i++) {
    labels[i] = PrintDate(labels[i], format);
  }

  // Get what to show infront of range (today, date, nothing)
  let what = '';
  if (first_ts.format('L') === last_ts.format('L'))
    what =
      momentWithLocale().format('L') === first_ts.format('L')
        ? 'history_today'
        : first_ts.utc().format('ll');

  return {
    graph: { labels: labels, datasets: datasets },
    vp: { what: what, from: PrintDate(first_ts, view_format), to: PrintDate(last_ts, view_format) }
  };
}

function ReduceData(graph, threshold, goal) {
  const cur_length = graph.datasets[0].data.length;
  const labels = graph.labels;
  const datasets = graph.datasets;

  // If threshold not reached, don't do anything
  if (cur_length < threshold) return graph;

  // Create new empty arrays for labels and data.
  let new_labels = [];
  let new_data = [];
  for (var i = 0; i < datasets.length; i++) new_data.push([]);
  const diff = Math.floor(cur_length / goal);

  // Fill new arrays with reduced data.
  for (let i = 0; i < cur_length; i += diff) {
    // handle labels
    const selection = labels.slice(i, i + diff);
    new_labels.push(selection[Math.floor(selection.length / 2)]);
    // handle datasets
    for (let j = 0; j < datasets.length; j++) {
      const selection = datasets[j].data.slice(i, i + diff);
      new_data[j].push(selection[Math.floor(selection.length / 2)]);
    }
  }
  // Update datasets
  for (let i = 0; i < datasets.length; i++) graph.datasets[i].data = new_data[i];
  graph.labels = new_labels;
  return graph;
}

// Valtio's `subscribe` does not work with `useState`
// we have to use also a valtio store for this
// I opened an issue for this https://github.com/pmndrs/valtio/discussions/857
const store = proxy({
  data: _.cloneDeep(inital_data),
  range: { start: 0, end: -1 },
  id: ''
});

export function Graph({ ...other }) {
  let { t } = useTranslation();
  const theme = useTheme();
  let { id, zone_id } = useParams();
  let { enqueueSnackbar } = useSnackbar();
  const address = useAddress();

  const stateManager = getStateManager();
  const { controller, parent } = stateManager.getControllerForTab({ id: id, zone_id: zone_id });
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const supportsStableTransferId = supportsStableHistoryTransferId(controller);
  const snap = useSnapshot(store);
  const data = snap.data;

  // initialize displayed selection
  const [show_data, setShowData] = useState(_.cloneDeep(inital_data));

  // View range.
  const [displayVP, setDisplayVP] = useState({ what: 'Today', from: '', to: '' }); // displays visible range.
  const [range_slider_value, setRangeSliderValue] = React.useState([
    0,
    data.datasets[0].data.length
  ]); // current slider value.
  const historyRetryTimeoutRef = useRef(null);
  const historyInactivityTimeoutRef = useRef(null);
  const preservePublishedHistoryRef = useRef(false);
  const updateRangeSliderValue = (event, new_value) => {
    setRangeSliderValue(new_value);
  };

  const updateRange = (value) => {
    if (value[0] < value[1]) {
      store.range.start = value[0];
      store.range.end = value[1] === store.data.datasets[0].data.length ? -1 : value[1];
    }
  };

  const cancelHistoryRecoveryRetry = () => {
    if (historyRetryTimeoutRef.current !== null) {
      clearTimeout(historyRetryTimeoutRef.current);
      historyRetryTimeoutRef.current = null;
    }
  };

  const cancelHistoryInactivityWatchdog = () => {
    if (historyInactivityTimeoutRef.current !== null) {
      clearTimeout(historyInactivityTimeoutRef.current);
      historyInactivityTimeoutRef.current = null;
    }
  };

  const resetHistoryData = (resolution, { preservePublishedData = false } = {}) => {
    if (!preservePublishedData) {
      for (const type of [
        'time',
        'humidity',
        'temp',
        'co2',
        'vpd',
        'temp_leaf_current',
        'vol_water_content',
        'ec_root_zone',
        'vol_water_content_2',
        'ec_root_zone_2',
        'vol_water_content_3',
        'ec_root_zone_3',
        'vol_water_content_4',
        'ec_root_zone_4'
      ]) {
        parent.history.data[type] = [];
      }
      parent.history.data.last_ts = 0;
    }
    parent.history.data.progress = 0;
    parent.history.data.part = 0;
    parent.history.data.pending_data = [];
    parent.history.data.pending_transfer_id = null;
    parent.history.data.transfer_mode = 'unknown';
    if (resolution !== undefined) {
      parent.history.data.resolution = resolution;
    }
  };
  const requestHistoryData = (value, options = {}) => {
    const { preservePublishedData = false, recoveryRetry = false } = options;
    const normalizedValue = normalizeHistoryResolution(value);
    const replacingStableRequest =
      supportsStableTransferId && parent.history.data.request_in_flight;
    if (!recoveryRetry) {
      cancelHistoryRecoveryRetry();
      cancelHistoryInactivityWatchdog();
    }
    preservePublishedHistoryRef.current = preservePublishedData;
    if (normalizedValue === resolutions.recent) {
      console.debug(
        '[hist][recent][request]',
        'controller=',
        controller.id,
        'parent=',
        parent.id,
        'progress_before=',
        parent.history.data.progress,
        'points_before=',
        parent.history.data.time.length
      );
    }

    if (replacingStableRequest) abandonHistoryTransfer(parent.history.data);

    // Reset global state
    resetHistoryData(normalizedValue, { preservePublishedData });
    parent.history.data.incomplete_final_chunk = false;
    beginHistoryRequest(parent.history.data, normalizedValue, {
      recoveryRetry,
      preserveAbandonedTransfers: replacingStableRequest
    });

    const resolution_entry = resolution_mapping[normalizedValue];

    if (Number(value) !== normalizedValue) {
      console.warn('[graph] normalized resolution value', value, 'to', normalizedValue);
    } else {
      console.log('[graph] found entry for ', normalizedValue, resolution_entry);
    }

    let sent;
    if (controller.zones && controller.type >= 5155)
      sent = stateManager.wsManager.sendWSMsg(
        controller,
        resolution_entry.req,
        { zone_id: parent.id },
        false,
        true
      );
    else sent = stateManager.wsManager.sendWSMsg(controller, resolution_entry.req);

    if (!sent) {
      cancelHistoryRequest(parent.history.data);
      cancelHistoryInactivityWatchdog();
      preservePublishedHistoryRef.current = preservePublishedData;
    }
    return sent;
  };

  const armHistoryInactivityWatchdog = () => {
    cancelHistoryInactivityWatchdog();
    if (!supportsStableTransferId || !parent.history.data.request_in_flight) return;

    const activityAt = Number(parent.history.data.request_activity_at_ms ?? Date.now());
    const remaining = Math.max(0, HISTORY_INACTIVITY_TIMEOUT_MS - (Date.now() - activityAt));
    historyInactivityTimeoutRef.current = setTimeout(() => {
      historyInactivityTimeoutRef.current = null;
      if (!parent.history.data.request_in_flight) return;

      const latestActivityAt = Number(parent.history.data.request_activity_at_ms ?? Date.now());
      const inactiveFor = Date.now() - latestActivityAt;
      if (inactiveFor < HISTORY_INACTIVITY_TIMEOUT_MS) {
        armHistoryInactivityWatchdog();
        return;
      }

      if ((Number(parent.history.data.recovery_retry_count) || 0) === 0) {
        const resolution = parent.history.data.resolution;
        console.warn(
          '[graph] retrying stalled history transfer after inactivity',
          parent.id,
          resolution,
          'inactive_ms',
          inactiveFor
        );
        requestHistoryData(resolution, {
          preservePublishedData: true,
          recoveryRetry: true
        });
        armHistoryInactivityWatchdog();
        return;
      }

      console.warn(
        '[graph] history transfer stalled after recovery retry',
        parent.id,
        parent.history.data.resolution
      );
      cancelHistoryRequest(parent.history.data);
    }, remaining);
  };

  useEffect(() => {
    store.id = controller.id + '_' + parent.id;
    if (parent.history.data.progress !== 100 && !parent.history.data.request_in_flight) {
      const res =
        parent.history.data.resolution !== undefined ? parent.history.data.resolution : 240;
      console.log('[graph] requesting for res: ', res);
      requestHistoryData(res);
    }
  }, []);

  // Update full-data, if histroy was changed
  useEffect(() => {
    // Reacts to updated full-data and setting (vp-range) and parses the values to show.
    function reduce() {
      console.log(
        '[graph] reducing ',
        parent.id,
        parent.history.data.humidity.length,
        JSON.stringify(parent.history.data.humidity),
        store.data.datasets[0].data.length
      );
      const datasets = _.cloneDeep(store.data.datasets);
      const labels = _.cloneDeep(store.data.labels);
      const end = store.range.end === -1 ? store.data.datasets[0].data.length : store.range.end;
      // Slice data to selected view
      const sliced_data = SliceData(
        labels,
        datasets,
        store.range.start,
        end,
        controller,
        parent.history.data.resolution
      );
      // reduce data if certain threshold is reached.
      const reduced_data = ReduceData(sliced_data.graph, 502, 400);
      setShowData(reduced_data);
      setDisplayVP(sliced_data.vp);
    }

    function update() {
      console.log(
        '[graph] updadting',
        parent.id,
        parent.history.data.time.length,
        JSON.stringify(parent.history.data.humidity),
        'before:',
        store.data.datasets[0].data.length
      );
      if (!_.isEmpty(parent.history.data)) {
        if (parent.history.data.progress === 100 && !parent.history.data.incomplete_final_chunk) {
          cancelHistoryRecoveryRetry();
          cancelHistoryInactivityWatchdog();
          preservePublishedHistoryRef.current = false;
        }

        const retryPending = historyRetryTimeoutRef.current !== null;
        if (
          supportsStableTransferId &&
          !retryPending &&
          shouldRetryHistoryRequest(parent.history.data)
        ) {
          cancelHistoryInactivityWatchdog();
          const resolution = parent.history.data.resolution;
          preservePublishedHistoryRef.current = true;
          console.warn(
            '[graph] scheduling one recovery retry for incomplete history transfer',
            parent.id,
            resolution,
            'delay_ms',
            HISTORY_RECOVERY_RETRY_DELAY_MS
          );
          historyRetryTimeoutRef.current = setTimeout(() => {
            historyRetryTimeoutRef.current = null;
            if (
              parent.history.data.resolution !== resolution ||
              !parent.history.data.request_in_flight ||
              !parent.history.data.incomplete_final_chunk
            ) {
              return;
            }
            requestHistoryData(resolution, {
              preservePublishedData: true,
              recoveryRetry: true
            });
          }, HISTORY_RECOVERY_RETRY_DELAY_MS);
          return;
        }

        if (
          supportsStableTransferId &&
          !retryPending &&
          parent.history.data.request_in_flight &&
          parent.history.data.incomplete_final_chunk &&
          parent.history.data.recovery_retry_count > 0
        ) {
          console.warn(
            '[graph] incomplete history transfer still failed after recovery retry',
            parent.id,
            parent.history.data.resolution
          );
          cancelHistoryRequest(parent.history.data);
          cancelHistoryInactivityWatchdog();
        }

        armHistoryInactivityWatchdog();

        if (
          parent.history.data.time.length > 0 &&
          parent.history.data.progress === 100 &&
          store.data.labels.length > 0 &&
          store.data.labels.length === parent.history.data.time.length &&
          store.data.labels[0] === parent.history.data.time[0] &&
          store.data.labels.at(-1) === parent.history.data.time.at(-1)
        ) {
          console.log(
            '[graph] detected possible double subscription: ',
            parent.id,
            '. Updating only show/hide options.'
          );
          for (const ds of store.data.datasets) {
            ds.hidden = parent.history.data.hidden[ds.label];
          }
        } else if (parent.history.data.time.length > 0 && parent.history.data.progress === 100) {
          console.log('[graph] Updating graph for controller: ', parent.id, ' (update)');

          // Recent history can grow incrementally. Daily/full should replace the
          // visible dataset wholesale so retries/reloads never truncate the range.
          const append_only =
            parent.history.data.resolution === resolutions.recent &&
            store.data.labels.length > 0 &&
            store.data.labels.at(-1) !== parent.history.data.time.at(-1);

          for (const ds of store.data.datasets) {
            if (append_only) {
              ds.data.push(parent.history.data[ds.label][parent.history.data[ds.label].length - 1]);
            } else {
              ds.data = _.cloneDeep(parent.history.data[ds.label]);
            }
            ds.hidden = parent.history.data.hidden[ds.label];
          }

          if (append_only) {
            store.data.labels.push(parent.history.data.time[parent.history.data.time.length - 1]);
          } else {
            store.data.labels = _.cloneDeep(parent.history.data.time);
          }

          let datasets = store.data.datasets.map((d) => d.data.length).filter((d) => d > 0);
          if (datasets.length > 0) {
            setRangeSliderValue(
              store.range.end === -1
                ? [store.range.start, datasets[0]]
                : [store.range.start, store.range.end]
            );
          }
        } else if (
          (preservePublishedHistoryRef.current ||
            parent.history.data.pending_data.length > 0 ||
            parent.history.data.incomplete_final_chunk) &&
          store.data.labels.length > 0
        ) {
          console.log('[graph] preserving last complete history during transfer:', parent.id);
        } else {
          console.log('[graph] Updating graph for controller: ', parent.id, ' (reset)');
          store.data = _.cloneDeep(inital_data);
          setRangeSliderValue([0, 0]);
          store.range.start = 0;
          store.range.end = -1;
        }
        reduce();
      }
      console.log('[graph] updadting', parent.id, 'after:', store.data.datasets[0].data.length);
    }

    // Main update subscriptions
    const unsubscripeHistory = subscribe(parent.history, update);
    const unsubscribeRange = subscribe(store.range, reduce);
    const unsubscribeData = subscribe(store.data, reduce);

    // Subscribtion on controller-id, to make sure data is reset
    function reset(new_id) {
      console.log('[graph] ', parent.id, 'cont id changed: ', new_id, '. resetting graph-data.');
      cancelHistoryRecoveryRetry();
      cancelHistoryInactivityWatchdog();
      cancelHistoryRequest(parent.history.data);
      preservePublishedHistoryRef.current = false;
      store.data = _.cloneDeep(inital_data);
      setRangeSliderValue([0, 0]);
      store.range.start = 0;
      store.range.end = -1;
      resetHistoryData();
    }
    const unsubscribeId = subscribeKey(store, 'id', (v) => reset(v));

    console.log('[graph] calling update now!', parent.id);
    update();

    return () => {
      console.log('[graph] unsubscripeHistory', parent.id);
      if (unsubscripeHistory) unsubscripeHistory();
      if (unsubscribeRange) unsubscribeRange();
      if (unsubscribeData) unsubscribeData();
      if (unsubscribeId) unsubscribeId();
      cancelHistoryRecoveryRetry();
      cancelHistoryInactivityWatchdog();
      preservePublishedHistoryRef.current = false;
    };
  }, []);

  const getOrCreateLegendList = (chart, id) => {
    const legendContainer = document.getElementById(id);
    let listContainer = legendContainer.querySelector('ul');
    if (!listContainer) {
      listContainer = document.createElement('ul');
      listContainer.style.padding = 0;
      legendContainer.appendChild(listContainer);
    }

    return listContainer;
  };

  const StoreHiddenToUserdata = async (hidden) => {
    await fetch(address.api_url('/api/set_graph_hidden'), {
      method: 'POST',
      body: JSON.stringify(hidden)
    }).then((response) => {
      if (response.ok) {
        console.log('[graph] hidden_set_success');
      } else {
        enqueueSnackbar(t('hidden_set_error'), { variant: 'error' });
      }
    });
  };

  const htmlLegendPlugin = {
    id: 'htmlLegend',
    afterUpdate(chart, args, options) {
      const ul = getOrCreateLegendList(chart, options.containerID);

      // Remove old legend items
      while (ul.firstChild) {
        ul.firstChild.remove();
      }

      // Reuse the built-in legendItems generator
      const items = chart.options.plugins.legend.labels.generateLabels(chart);

      items.forEach((item) => {
        if (
          ([5055, 5155, 6000].includes(chart.options.contType) ||
            (item.text !== 'vpd' && item.text !== 'temp_leaf_current')) &&
          ([5055, 5155, 6000].includes(chart.options.contType) ||
            (item.text !== 'vol_water_content' &&
              item.text !== 'ec_root_zone' &&
              item.text !== 'vol_water_content_2' &&
              item.text !== 'ec_root_zone_2' &&
              item.text !== 'vol_water_content_3' &&
              item.text !== 'ec_root_zone_3' &&
              item.text !== 'vol_water_content_4' &&
              item.text !== 'ec_root_zone_4'))
        ) {
          const li = document.createElement('li');
          li.style.alignItems = 'center';
          li.style.cursor = 'pointer';
          li.style.display = 'flex';
          li.style.flexDirection = 'row';
          li.style.marginLeft = '10px';
          li.style.marginBottom = '3px';

          li.onclick = () => {
            // Change hidden
            parent.history.data.hidden[item.text] = !parent.history.data.hidden[item.text];
            // Send request to store data in user-account
            var hidden = {
              internal_id: controller.id,
              field: item.text,
              hidden: parent.history.data.hidden[item.text]
            };
            StoreHiddenToUserdata(hidden);
          };

          // Color box
          const boxSpan = document.createElement('span');
          //boxSpan.style.background = item.fillStyle;
          boxSpan.style.color = item.strokeStyle;
          boxSpan.style.borderColor = item.strokeStyle;
          boxSpan.style.borderWidth = item.lineWidth + 'px';
          boxSpan.style.display = 'inline-block';
          boxSpan.style.height = '20px';
          boxSpan.style.marginRight = '10px';
          boxSpan.style.width = '20px';
          boxSpan.innerHTML = ReactDOMServer.renderToString(
            item.hidden ? <VisibilityOffIcon /> : <VisibilityIcon />
          );

          // Text
          const textContainer = document.createElement('p');
          textContainer.style.color = item.fillStyle;
          textContainer.style.margin = 0;
          textContainer.style.padding = 0;
          textContainer.style.textDecoration = item.hidden ? 'line-through' : '';

          const text = document.createTextNode(t(item.text));
          textContainer.appendChild(text);

          li.appendChild(boxSpan);
          li.appendChild(textContainer);
          ul.appendChild(li);
        }
      });
    }
  };

  const options = React.useMemo(() => {
    const darkMode = theme.palette.type === 'dark';
    const gridColor = darkMode ? 'rgba(232, 232, 232, 0.16)' : 'rgba(0, 0, 0, 0.1)';
    const axisColor = darkMode ? 'rgba(232, 232, 232, 0.72)' : theme.palette.text.secondary;
    const axisOptions = {
      display: 'auto',
      type: 'linear',
      ticks: { color: axisColor },
      grid: { color: gridColor, borderColor: gridColor }
    };

    return {
      contType: controller.type,
      responsive: true,
      elements: {
        point: {
          radius: 1
        }
      },
      scales: {
        low: { ...axisOptions, position: 'left' },
        high: { ...axisOptions, position: 'right' },
        xAxes: {
          ticks: {
            color: axisColor,
            maxRotation: 90,
            minRotation: 90,
            padding: 4,
            font: {
              size: 10
            }
          },
          grid: { color: gridColor, borderColor: gridColor }
        }
      },
      plugins: {
        htmlLegend: { containerID: 'legend-container' },
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: function (tooltipItem) {
              const dataset = tooltipItem.dataset;
              let blocks = [];
              if (dataset.label) blocks.push(t(dataset.label) + ':');
              blocks.push(tooltipItem.formattedValue);
              if (dataset.unitPrefix) blocks.push(dataset.unitPrefix);
              return blocks.join(' ');
            }
          }
        }
      }
    };
  }, [controller.type, t, theme.palette.text.secondary, theme.palette.type]);

  const [open_export_dialog, setOpenExportDialog] = React.useState(false);
  const openExportDialog = () => {
    setOpenExportDialog(true);
  };
  const closeExportDialog = () => {
    setOpenExportDialog(false);
  };

  console.log(
    '[graph]',
    parent.id,
    parent.history.data.humidity.length,
    show_data.datasets[0].data.length
  );

  const [confirm_action_dialog_infos, setOpenConfirmActionDialog] = React.useState({
    open: false,
    type: '',
    value: undefined
  });
  const openConfirmActionDialog = (type, value) => {
    setOpenConfirmActionDialog({ open: true, type: type, value: value });
  };
  const closeConfirmActionDialog = () => {
    setOpenConfirmActionDialog({ open: false, type: '', value: -1 });
  };

  return (
    <React.Fragment>
      <ConfirmActionDialog
        t={t}
        stateManager={stateManager}
        open={confirm_action_dialog_infos.open}
        handleClose={closeConfirmActionDialog}
        controller={controller}
        parent={parent}
        action_type={confirm_action_dialog_infos.type}
        action_value={confirm_action_dialog_infos.value}
      />
      <ExportDialog
        open={open_export_dialog}
        handleClose={closeExportDialog}
        controllerHistory={parent.history.data}
        id={id}
      />
      <Typography variant="subtitle2" align="center" style={{ marginTop: `1rem` }}>
        {parent.history.data.progress === 100 ? (
          <i>
            {t('history_showing')} {t(displayVP.what)} {displayVP.from} {t('history_to')}{' '}
            {displayVP.to}
          </i>
        ) : data.datasets[0].data.length > 0 ? (
          <i>loading...</i>
        ) : parent.history.data.progress === 0 ? (
          <i>no data</i>
        ) : (
          <i>loading...</i>
        )}
      </Typography>
      <Box display="flex" alignItems="center" justifyContent="flex-start">
        <HistoryResSelect
          dailyLabelKey={getHistoryTranslationKey(controller, 'history_type_daily')}
          value={parent.history.data.resolution}
          onChange={(event, value) => requestHistoryData(event.target.value)}
        />
        <Button
          onClick={() =>
            requestHistoryData(parent.history.data.resolution, { preservePublishedData: true })
          }
        >
          {t('refresh')}
        </Button>
        <Button onClick={openExportDialog}>{t('export')} (CSV)</Button>
      </Box>

      {parent.history.data.progress === 100 || data.datasets[0].data.length > 0 ? (
        <MemorizedChart data={show_data} options={options} plugins={[htmlLegendPlugin]} />
      ) : (
        <Box sx={{ with: '80%', margin: 2 }}>
          <LinearProgressWithLabel value={parent.history.data.progress} />
        </Box>
      )}

      {data.datasets[0].data.length > 0 && (
        <Box sx={{ mt: '1rem', px: { xs: 2, sm: 0 } }}>
          <GraphSlider
            value={range_slider_value}
            min={0}
            max={data.datasets[0].data.length}
            onChange={updateRangeSliderValue}
            onChangeCommitted={(event, value) => updateRange(value)}
          />
        </Box>
      )}

      <div id="legend-container" style={{ marginBottom: '30px' }} />

      <ActionButton
        title={connectionDisabledReason}
        color="secondary"
        disabled={connectionStatus !== 'good'}
        onClick={() => openConfirmActionDialog('action_erase_daily_history', undefined)}
      >
        {t(getHistoryTranslationKey(controller, 'erase_daily_history'))}
      </ActionButton>
      <ActionButton
        title={connectionDisabledReason}
        color="secondary"
        disabled={connectionStatus !== 'good'}
        onClick={() => openConfirmActionDialog('action_erase_full_history', undefined)}
      >
        {t('erase_full_history')}
      </ActionButton>
      <Typography variant="caption" display="block" color="textSecondary">
        {t('erase_history_new_rootzone_values_info')}
      </Typography>
    </React.Fragment>
  );
}

function ConfirmActionDialog({
  stateManager,
  t,
  open,
  handleClose,
  controller,
  parent,
  action_type,
  action_value
}) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { enqueueSnackbar } = useSnackbar();

  const doAction = (event) => {
    if (controller.connection?.status !== 'good') return;
    // If parent.id is controller id then this Graph is opend by non-zoned
    // controller =>
    const zone_id = parent.id === controller.id ? undefined : parent.id;
    HandleAction(
      stateManager.wsManager,
      controller,
      action_type,
      action_value,
      zone_id,
      t,
      enqueueSnackbar
    );
    handleClose();
  };

  return (
    <DialogWithEnterAction
      enterActionDisabled={connectionStatus !== 'good'}
      open={open}
      onClose={handleClose}
      onEnterAction={doAction}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('confirm_action_title')}</DialogTitle>
      <DialogContent>
        {t(getHistoryTranslationKey(controller, action_type + '_text'))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <ActionButton
          title={connectionDisabledReason}
          disabled={connectionStatus !== 'good'}
          onClick={doAction}
          color="primary"
        >
          {t('apply')}
        </ActionButton>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function ExportDialog({ open, handleClose, controllerHistory, id }) {
  let { t } = useTranslation();
  const [time, setTime] = React.useState('DD.MM.YYYY HH:mm:ss');
  const [delimiter, setDelimiter] = React.useState(';');
  const [decimal, setDecimal] = React.useState(',');

  const UpdateDelimiter = (value) => {
    if (value === ',' && decimal === ',') setDecimal('.');
    setDelimiter(value);
  };

  const validateForm = (event) => {
    if (controllerHistory.time.length > 0) {
      let csv_content = 'data:text/csv;charset=utf-8,';
      let names = ['time'];
      names.push(...HISTORY_VALUES);
      csv_content += names.join(delimiter) + '\n';

      for (let i = 0; i < controllerHistory.time.length; i++) {
        for (const field of names) {
          if (field === 'time')
            csv_content +=
              momentWithLocale(controllerHistory[field][i]).utc().format(time) + delimiter;
          else if (field === 'ec_root_zone_4')
            csv_content += ParseFloat(controllerHistory[field][i], decimal) + '\n';
          else csv_content += ParseFloat(controllerHistory[field][i], decimal) + delimiter;
        }
      }
      var encodedUri = encodeURI(csv_content);
      var link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', momentWithLocale().utc().format() + '_' + id + '.csv');
      document.body.appendChild(link);
      link.click();
    }
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={validateForm}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('export')}</DialogTitle>
      <DialogContent>
        <label>{t('time_format')}: </label>
        <br />
        <TimeFormatSelect value={time} onChange={(event) => setTime(event.target.value)} />
        <br />
        <br />
        <label>{t('delimiter')}: </label>
        <br />
        <DelimiterSelect
          value={delimiter}
          onChange={(event) => UpdateDelimiter(event.target.value)}
        />
        <br />
        <br />
        <label>{t('decimal_format')}: </label>
        <br />
        <DecimalSelect
          value={decimal}
          delimiter={delimiter}
          onChange={(event) => setDecimal(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={validateForm} color="primary">
          {t('set')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function LinearProgressWithLabel(props) {
  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress variant="determinate" {...props} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          {props.value >= 0 && props.value <= 100 ? (
            <Typography variant="body2" color="text.secondary">
              {`${Math.round(props.value)}%`}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">{`--- %`}</Typography>
          )}
        </Box>
      </Box>
      {props.value === 0 && (
        <Typography value="body" color="text.secondary">
          Might be waiting for other data...
        </Typography>
      )}
    </React.Fragment>
  );
}

export const MemorizedGraph = React.memo(Graph);
